'use client';
/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useRef,
  useState,
  useCallback,
  RefObject,
  useEffect,
  useMemo,
} from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { Element } from 'react-scroll';
import { v4 as uuid } from 'uuid';
import { useParams } from 'next/navigation';

import ScrollToTop from '@shared/ScrollToTop';
import { useBreakpoint } from '@contexts/breakpoints';
import MultipleImageView from '@components/galleries/multiple-image/MultipleImageView';
import SummaryCard from '@components/cards/summaries/hotel/SummaryCard';
import Breadcrumb from '@components/Breadcrumb/Breadcrumb';
import { Row, Separator } from '@components/formStyles';
import {
  BreadcrumbWrapper,
  ImagesWrapper,
  Wrapper,
  Container,
  RoomWarning,
  HotelLocationWrapper,
  BasketErrorText,
  Seperator,
  Line,
  TabMenuWrapper,
  AnnouncementWrapper,
  TourismCertificateWrapper,
  TourismCertificateText,
  Title,
} from '@components/views/hotel/detail/HotelDetailStyle';
import RoomOptions from '@components/views/hotel/detail/components/RoomOptions';
import Loader from '@components/Loader/Loader';
import { formatArrayToString, getRoomInformations } from '@utils/utils';
import { ApiError, LoyaltyType, ProviderType, RoomViewModel } from '@api/index';
import InfoNote from '@components/ui/InfoNote';
import { getTypedFeatureProps } from '@views/hotel/detail/utils/features';
import HotelDetailSkeleton from '@components/views/hotel/detail/components/skeleton/Skeleton';
import BankInstallments from '@components/ui/bank-installments/BankInstallments';
import ErrorPage from '@components/views/error/ErrorPage';
import { addHotelDetailGTM } from '@utils/gtm/hotel-gtm';
import useUnlockBasket from '@components/views/hooks/useUnlockBasket';
import { useIsKocAilemValidated } from '@hooks/cache';
import { HotelDetailSchemaOrg } from '@utils/schemaorg/HotelDetailSchemaOrg';
import { useToggle } from '@hooks/common';
import ConfirmationModal from '@components/ui/modals/ConfirmationModal';
import useBasketInformation from '@hooks/useBasketInformation';
import { clearBasket } from '@utils/clearBasket';
import { usePageTransitionAnimation } from '@hooks/animations';
import ErrorInfoModal from '@components/views/components/ErrorInfoModal/ErrorInfoModal';
import { callService } from '@api/caller';
import { clearBasketStorage } from '@utils/basket';
import useSearchBar from '@contexts/searchbar';
import { HotelSearchBarActionType } from '@contexts/searchbar/types/hotel';
import CustomIcon from '@components/utils/CustomIcon';
import { ArrowLeftLine } from '@assets/icons';
import TabMenu from '@shared/searchBar/tabMenu';
import palette from '@utils/theme/palette';
import { useAppRouter } from '@contexts/router';
import { useSearchParamsToQuery } from '@hooks/useSearchParamsToQuery';
import { PatchedLink } from '@utils/patched-link';

import AboutUs from './components/about-us/AboutUs';
import Rules from './components/hotelDetailRules/Rules';
import LoyaltyVerifications from './components/loyalty-verifications/LoyaltyVerifications';
import DetailsAndGalleries, {
  ModalView,
} from './components/details-galleries-modal';
import PriceDetails, {
  PriceDetailsDrawerItem,
} from '../components/PriceDetails';
import {
  useHotelDetailState,
  useSetModalLoyaltyType,
  useSetSelectedVariations,
} from './context';
import { formatHotelImages } from './utils/formats';
import HotelLocation from './components/hotel-location/HotelLocation';
import { groupImagesByCategory } from './helpers';
import Announcements from './announcements/Announcements';
import { useHotelReviews } from './data-loader';
import HotelDetailScoring from './scoring/HotelDetailScoring';
import HotelListMobileHeader from '../listing/components/HotelListMobileHeader';
import { Entity } from './components/details-galleries-modal/types';
import { RoomOptionDetailProps } from './components/RoomOptions/RoomOptionDetails';
import HotelFeatures from './components/hotel-features';
import { useGetHotelRecommendations } from '../hooks/useGetHotelRecommendations';
import HotelRecommendations from './components/hotel-recommendations/HotelRecommendations';
import {
  getHotelDetailTabMenuList,
  getHotelDetailTabChangeHandler,
} from './utils/tabMenu';
import HotelEvents from './components/events/HotelEvents';
import { buildBackendRoomArray } from '../utils';

type ModalViewInfo = {
  galleriesDetailsModalShown: boolean;
  view: ModalView;
  entityType: Entity;
  roomOptionDetails?: RoomOptionDetailProps;
};

type AddedRoomParams = {
  roomTypeId: string;
};
export interface HotelRooms extends RoomViewModel, AddedRoomParams {}

const HotelDetail = () => {
  const query = useSearchParamsToQuery();
  const router = useAppRouter();

  const setModalLoyaltyType = useSetModalLoyaltyType();

  const params = useParams();
  const slug = params?.slug;
  const { in: startDate, out: endDate, room = ['2'] } = query;

  const { xl } = useBreakpoint();
  const { loyaltyType, selectedVariations, unSelectedRooms } =
    useHotelDetailState();

  const setSelectedVariations = useSetSelectedVariations();
  const { dispatch } = useSearchBar();

  const featuresRef = useRef<HTMLElement>(null);
  const eventsRef = useRef<HTMLElement>(null);
  const roomsRef = useRef<HTMLElement>(null);
  const aboutsRef = useRef<HTMLElement>(null);
  const locationRef = useRef<HTMLElement>(null);
  const announcementsRef = useRef<HTMLElement>(null);
  const scoringRef = useRef<HTMLElement>(null);

  const { basketInfo, basketId, itemsInBasket } = useBasketInformation();
  const [showInstallments, setShowInstallments] = useState<boolean>(false);
  const [hotelRooms, setHotelRooms] = useState<HotelRooms[]>([]);
  const [errorModalStatusMessage, setErrorModalStatusMessage] =
    useState<string>('');

  const [
    confirmFullBasketModal,
    toggleConfirmFullBasketModal,
    setConfirmFullBasketModal,
  ] = useToggle();
  const [isRemoveFlightLoading, setIsRemoveFlightLoading] = useState(false);

  const isValidatedKocAilem = useIsKocAilemValidated();
  const showKocAilem = useMemo(() => {
    return isValidatedKocAilem ? false : query?.utm_source === 'kocailem';
  }, []);

  const [modalViewInfo, setGalleryDetailsModalViewInfo] =
    useState<ModalViewInfo>({
      galleriesDetailsModalShown: false,
      view: ModalView.NONE,
      entityType: 'hotel',
      roomOptionDetails: undefined,
    });

  const [viewedRoom, setViewedRoom] = useState<RoomViewModel | undefined>(
    undefined
  );

  const viewRoom = useCallback(
    (
      room: RoomViewModel,
      view: ModalView,
      entityType?: Entity,
      roomOptionDetails?: RoomOptionDetailProps
    ) => {
      setViewedRoom(room);
      setGalleryDetailsModalViewInfo({
        galleriesDetailsModalShown: true,
        view,
        entityType: entityType || 'room',
        roomOptionDetails,
      });
    },
    []
  );

  useEffect(() => {
    showKocAilem && setModalLoyaltyType(LoyaltyType.KOC_AILEM);
  }, []);

  const {
    data,
    isLoading,
    refetch,
    isError: isGetHotelDetailError,
  } = useQuery(
    ['hotelDetail', JSON.stringify(query)],
    () => {
      /*  TODO (mert.ohancan) should be removed when backend team removed basketId on the request parameters */
      const basketId =
        typeof window !== 'undefined' && localStorage.getItem('basketId');
      const formattedRoom = buildBackendRoomArray(query?.room);

      return callService('HotelsService', 'getHotel', {
        slug: encodeURIComponent(String(slug)),
        room: formattedRoom,
        ...(startDate && endDate
          ? {
              _in: formatArrayToString(startDate),
              out: formatArrayToString(endDate),
            }
          : {}),
        ...(basketId ? { basketId } : {}),
      });
    },
    { enabled: Boolean(slug) }
  );

  const { hotelRecommendations } = useGetHotelRecommendations(
    data,
    formatArrayToString(startDate || ''),
    formatArrayToString(endDate || '')
  );

  const [hotelGalleries, setHotelGalleries] = useState<
    ReturnType<typeof groupImagesByCategory>
  >({});
  useEffect(() => {
    setHotelGalleries(
      groupImagesByCategory(
        data?.images ?? [],
        'hotelImageCategoryName',
        data?.name || 'Setur'
      )
    );
  }, [data?.images]);

  const [viewedRoomGallery, setViewedRoomGallery] = useState<
    ReturnType<typeof groupImagesByCategory>
  >({});
  useEffect(() => {
    setViewedRoomGallery(
      groupImagesByCategory(viewedRoom?.images ?? [], '', 'Oda Fotoğrafı')
    );
  }, [viewedRoom?.images]);

  const { isLoading: isRedirecting, hideLoading } =
    usePageTransitionAnimation();
  const { hotelReviews } = useHotelReviews(data?.id || 0);

  const { unlockBasketAsync, cleanBasketSignal } = useUnlockBasket({
    basketId: basketId || '',
  });

  const addBasket = async () => {
    const basketId =
      typeof window !== 'undefined' && localStorage.getItem('basketId');

    const body = selectedVariations.map((room) => ({
      hotelId: data?.id || 0,
      roomId: room.roomId,
      in: formatArrayToString(startDate || ''),
      out: formatArrayToString(endDate || ''),
      cancellationPolicyId: room.cancelletionPolicyId,
      boardTypeId: room.boardTypeId,
      provider: room.provider,
      guest: {
        adultCount: room.guests.adultCount || 0,
        childBirthdates: room.guests.childrenBirthDates || [''],
      },
      guestSearch: {
        adultCount: data?.guests?.adultCount || 0,
        childBirthdates: data?.guests?.childrenBirthDates || [''],
      },
      extraServices: [],
      promotions: [room.promotionId],
      variationId: room.variationId,
      loyaltyType: room.loyaltyType || undefined,
      roomTypeId:
        room.provider === ProviderType.TATIL_BUDUR
          ? room.roomTypeId
          : (null as unknown as undefined),
      boardTypeName: room.boardTypeName,
    }));

    await unlockBasketAsync();

    const basketState = await cleanBasketSignal;

    return callService('PrebookService', 'prebookAddHotel', {
      basketId: basketState === 'stale' || !basketId ? '' : basketId,
      body,
    });
  };

  const prebookMutation = useMutation(['hotelPrebook'], addBasket, {
    onSuccess: (res) => {
      if (res?.basketId) {
        if (typeof window !== 'undefined')
          localStorage.setItem('basketId', res.basketId);
        router.push(`/seyahat-planlama?bid=${res.basketId}`);
      }
    },
    onError: (error: ApiError) => {
      hideLoading();
      setErrorModalStatusMessage(error?.body?.errors?.[0]?.detail);
    },
  });

  const drawerItems = useCallback(
    (): PriceDetailsDrawerItem[] =>
      selectedVariations.map((variation) => {
        return {
          id: variation.variationId,
          name: variation.promotionInfoName,
          price: variation.totalDiscountedSellingPrice,
          tabNumber: variation.tabNumber,
          milePrice: variation.milePrice,
        };
      }),
    [selectedVariations]
  );

  const handleRemoveVariation = (id: string): void =>
    setSelectedVariations(
      selectedVariations.filter((room) => room.variationId !== id)
    );

  const handleNavigateFeatures = (): void =>
    featuresRef?.current?.scrollIntoView({ behavior: 'smooth' });

  const handleNavigateEvents = (): void =>
    eventsRef?.current?.scrollIntoView({ behavior: 'smooth' });

  const handleNavigateRooms = (): void =>
    roomsRef?.current?.scrollIntoView({ behavior: 'smooth' });

  const handleNavigateAboutUs = (): void =>
    aboutsRef?.current?.scrollIntoView({ behavior: 'smooth' });

  const handleNavigateLocation = (): void =>
    locationRef?.current?.scrollIntoView({ behavior: 'smooth' });

  const handleClickTripAdvisor = (): void => {
    scoringRef?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const handleNavigateTravelPlanning = async (): Promise<void> =>
    prebookMutation.mutate();

  const handleLoyaltySubmit = () => {
    refetch();
    setModalLoyaltyType(null);
  };

  const toggleInstallments = (): void => setShowInstallments((state) => !state);

  const detailsAndGalleriesProps = {
    galleries:
      modalViewInfo.entityType === 'hotel' ? hotelGalleries : viewedRoomGallery,
    title:
      modalViewInfo.entityType === 'hotel'
        ? data?.name
        : `${viewedRoom?.name},${viewedRoom?.typeName}`,
    buttonActionMessage:
      modalViewInfo.entityType === 'hotel' ? 'Odaları Gör' : 'Oda Seç',
    roomOptionDetailProps: modalViewInfo.roomOptionDetails,
  };

  const featuresProps = getTypedFeatureProps({
    entityType: modalViewInfo.entityType || 'hotel',
    features:
      modalViewInfo.entityType === 'hotel'
        ? data?.features ?? []
        : viewedRoom?.features ?? [],
  });
  useEffect(() => {
    if (data) {
      addHotelDetailGTM(data);
    }
  }, [data]);

  useEffect(() => {
    if (!data?.rooms || data?.rooms.length === 0) return;
    const rooms: HotelRooms[] = [];
    data?.rooms?.forEach((room) => {
      rooms.push({
        ...room,
        roomTypeId: uuid(),
      });
    });

    setHotelRooms(rooms);
  }, [data?.rooms]);

  // =========================================
  // Clear basket hotel items before add new one
  // =========================================
  const handleClearBasketItems = async () => {
    try {
      if (basketInfo && basketId) {
        setIsRemoveFlightLoading(true);

        if (basketInfo?.id && basketInfo.isLocked) {
          clearBasketStorage(true);
        } else {
          await unlockBasketAsync();
          await clearBasket(basketInfo, basketId);
        }

        toggleConfirmFullBasketModal();
        handleNavigateTravelPlanning();
      }
    } catch (error) {
      toggleConfirmFullBasketModal();
      clearBasketStorage(true);
    } finally {
      setIsRemoveFlightLoading(false);
    }
  };

  const errorInfoModalClosed = () => {
    setErrorModalStatusMessage('');
  };

  const filteredEvents = useMemo(() => {
    const events = data?.events || [];
    if (!events.length) return [];
    if (!query?.in) return events;
    const inTime = new Date(query.in as string).getTime();
    return events.filter((event) =>
      event.dates?.some((date) => date && new Date(date).getTime() >= inTime)
    );
  }, [data?.events, query?.in]);

  const hotelDetailTabMenuList = useMemo(
    () =>
      getHotelDetailTabMenuList({
        hasEvents: filteredEvents.length > 0,
        hasFeatures: Boolean(data?.seasonFeatures?.length),
      }),
    [filteredEvents.length, data?.features?.length]
  );

  const handleTabChange = useMemo(
    () =>
      getHotelDetailTabChangeHandler(hotelDetailTabMenuList, {
        overview: () => window.scrollTo({ top: 0, behavior: 'smooth' }),
        events: () => handleNavigateEvents(),
        features: () => handleNavigateFeatures(),
        rooms: () => handleNavigateRooms(),
        location: () => handleNavigateLocation(),
        scoring: () => handleClickTripAdvisor(),
        about: () => handleNavigateAboutUs(),
      }),
    [hotelDetailTabMenuList]
  );

  if (isGetHotelDetailError) return <ErrorPage />;

  const beginDate = new Date(formatArrayToString(startDate || ''));

  return (
    <Container>
      {!selectedVariations.length ? <ScrollToTop /> : null}
      {data ? <HotelDetailSchemaOrg hotelDetail={data} /> : null}
      {data?.bankInstallments && showInstallments ? (
        <BankInstallments
          bankInstallments={data.bankInstallments}
          onClose={toggleInstallments}
        />
      ) : null}
      {loyaltyType ? (
        <LoyaltyVerifications
          action={handleLoyaltySubmit}
          loyaltyType={loyaltyType}
          onCancel={() => setModalLoyaltyType(null)}
        />
      ) : null}

      {!xl ? <HotelListMobileHeader /> : null}

      {!xl && !data?.breadcrumbs?.[0]?.url ? null : (
        <BreadcrumbWrapper>
          <PatchedLink href={data?.breadcrumbs?.[0]?.url}>
            <Row alignItems='center'>
              <CustomIcon
                icon={ArrowLeftLine}
                fontSize={xl ? 24 : 16}
                paletteColor='grey800'
              />
              Tüm Sonuçları Gör
              {xl ? <Seperator /> : null}
            </Row>
          </PatchedLink>

          {xl ? (
            <Row>
              <Breadcrumb items={data?.breadcrumbs || []} />
            </Row>
          ) : null}
        </BreadcrumbWrapper>
      )}

      <Line />

      {isLoading || !data ? (
        <HotelDetailSkeleton />
      ) : (
        <>
          <ImagesWrapper>
            {data?.tourismCertificateText && data?.tourismCertificateNumber ? (
              <TourismCertificateWrapper>
                <TourismCertificateText>
                  Kültür ve Turizm Bakanlığı - {data.tourismCertificateText}:{' '}
                  {` `}
                  {data.tourismCertificateNumber}
                </TourismCertificateText>
              </TourismCertificateWrapper>
            ) : null}
            <MultipleImageView
              isSafe={data.secure}
              isSuggested={data.suggests}
              images={formatHotelImages(data.images, data.name)}
              totalImages={data.images?.length}
              isGalleryOpen={modalViewInfo.view !== ModalView.NONE}
              viewGallery={() =>
                setGalleryDetailsModalViewInfo({
                  galleriesDetailsModalShown: true,
                  view: ModalView.GALLERIES,
                  entityType: 'hotel',
                })
              }
            />
          </ImagesWrapper>

          <Wrapper>
            <TabMenuWrapper>
              <TabMenu
                tabs={hotelDetailTabMenuList}
                onTabChange={handleTabChange}
                defaultTabId={1}
                colors={{
                  passiveTextColor: palette.grey800,
                }}
              />
            </TabMenuWrapper>
            <Element name='overview' className='element'>
              <SummaryCard
                name={data.name || ''}
                description={data.description || ''}
                boardType={data.boardType || ''}
                prices={data.lowestPrice}
                onNavigate={handleNavigateRooms}
                onReadMore={handleNavigateAboutUs}
                onClickTripAdvisor={handleClickTripAdvisor}
                roomInformation={
                  getRoomInformations(
                    formatArrayToString(room),
                    formatArrayToString(startDate || []),
                    formatArrayToString(endDate || [])
                  ).message
                }
                address={data.address}
                expedia={data.isExpedia}
                ratings={hotelReviews}
              />
            </Element>

            <Separator size='100px' />

            {filteredEvents.length ? (
              <>
                <Element name='events' className='element'>
                  <div ref={eventsRef as RefObject<HTMLDivElement>}>
                    <Title>Otel Etkinlikleri</Title>
                    <Separator />
                    <HotelEvents data={filteredEvents} />
                  </div>
                  <Separator />
                </Element>
              </>
            ) : null}

            <Separator size='100px' />

            <Element name='features' className='element'>
              <div ref={featuresRef as RefObject<HTMLDivElement>}>
                <Title>Otel Özellikleri</Title>
                <Separator />
                {data.seasonFeatures?.length ? (
                  <HotelFeatures
                    hotelFeatures={data.seasonFeatures}
                    beginDate={beginDate}
                    generalConditions={data.generalConditions}
                  />
                ) : null}
              </div>
            </Element>
            <Separator size='100px' />
            <Element name='rooms' className='element'>
              <div ref={roomsRef as RefObject<HTMLDivElement>}>
                {data.nightCount && !data?.rooms?.length ? (
                  <RoomWarning>
                    <InfoNote>
                      Belirtilen tarihlerde otel müsait görünmüyor.
                    </InfoNote>
                  </RoomWarning>
                ) : (
                  <RoomOptions
                    hotelId={data.id}
                    hotelName={data.name || ''}
                    countryCode={data.countryCode}
                    items={hotelRooms ?? []}
                    nightCount={data.nightCount}
                    roomNumber={
                      typeof room === 'string'
                        ? room.split(',').length
                        : room?.length || 1
                    }
                    viewRoom={viewRoom}
                    isExpedia={data.isExpedia}
                    openDatePicker={() => {
                      dispatch({
                        type: HotelSearchBarActionType.SET_IS_HOTEL_DATE_SELECTED,
                        payload: true,
                      });
                      handleNavigateRooms();
                    }}
                  />
                )}
              </div>
            </Element>

            <Separator size='100px' />

            <Element name='about' className='element'>
              {data.description ? (
                <div ref={aboutsRef as RefObject<HTMLDivElement>}>
                  <AboutUs description={data.description} />
                  <Separator size='100px' />
                </div>
              ) : null}
            </Element>
            <Separator size='100px' />

            <Element name='location' className='element'>
              {data.latitude && data.longitude ? (
                <div ref={locationRef as RefObject<HTMLDivElement>}>
                  <HotelLocationWrapper>
                    <HotelLocation
                      locationName={data.name}
                      locationAddress={data.address}
                      latitude={data.latitude}
                      longitude={data.longitude}
                    />
                  </HotelLocationWrapper>
                </div>
              ) : null}
            </Element>

            <Separator size='100px' />
            <Element name='scoring' className='element'>
              <div ref={scoringRef as RefObject<HTMLDivElement>}>
                {hotelReviews?.rating && (hotelReviews?.rating || 0) >= 3 ? (
                  <HotelDetailScoring
                    reviews={hotelReviews?.reviews}
                    subratings={hotelReviews?.subratings}
                    tripTypes={hotelReviews?.tripTypes}
                    rating={hotelReviews?.rating}
                    provider={hotelReviews.provider}
                  />
                ) : null}
              </div>
            </Element>

            <Separator size='100px' />

            {data?.checkinBeginTime ? (
              <div>
                <Element name='rules' className='element'>
                  <Rules
                    checkinBeginTime={data.checkinBeginTime}
                    checkinEndTime={data.checkinEndTime}
                    mandatoryFee={data.mandatoryFee}
                    optionalFee={data.optionalFee}
                    checkoutTime={data.checkoutTime}
                  />
                </Element>
                <Separator size='100px' />
              </div>
            ) : null}

            <Element name='announcements' className='element'>
              {data.announcements?.length > 0 ? (
                <AnnouncementWrapper
                  ref={announcementsRef as RefObject<HTMLDivElement>}
                >
                  <Announcements data={data.announcements} />
                </AnnouncementWrapper>
              ) : null}
            </Element>

            {hotelRecommendations?.data?.length ? (
              <HotelRecommendations data={hotelRecommendations.data} />
            ) : null}
          </Wrapper>

          {selectedVariations.length ? (
            <PriceDetails
              items={drawerItems()}
              onNavigate={handleNavigateTravelPlanning}
              onRemove={handleRemoveVariation}
              currency={data.lowestPrice?.currencyCode}
              warningMessage={
                unSelectedRooms?.length
                  ? `Devam etmek için ${unSelectedRooms[0]}. odayı seçiniz`
                  : undefined
              }
              toggleConfirmModal={toggleConfirmFullBasketModal}
              itemsInBasket={itemsInBasket || 0}
            />
          ) : null}

          {confirmFullBasketModal ? (
            <ConfirmationModal
              onCancel={() => setConfirmFullBasketModal(false)}
              onSubmit={handleClearBasketItems}
              content='Sepetinizde başka bir ürün bulunmakta, var olanı silip yenisini eklemek ister misiniz?'
              title='Sil'
              isConfirmDisabled={isRemoveFlightLoading}
              isConfirmLoading={isRemoveFlightLoading}
            />
          ) : null}

          {prebookMutation.isLoading || isRedirecting ? <Loader /> : null}

          {prebookMutation.isError ? (
            <ErrorInfoModal
              title='Ooops'
              description={
                <BasketErrorText>
                  {errorModalStatusMessage !== ''
                    ? errorModalStatusMessage
                    : 'Sepete ürün eklemeye çalışırken, genel bir hata oluştu, tekrar deneyiniz.'}
                </BasketErrorText>
              }
              onCancel={errorInfoModalClosed}
            />
          ) : null}

          {modalViewInfo.galleriesDetailsModalShown ? (
            <DetailsAndGalleries
              {...detailsAndGalleriesProps}
              {...featuresProps}
              closeModal={() => {
                setGalleryDetailsModalViewInfo({
                  galleriesDetailsModalShown: false,
                  view: ModalView.NONE,
                  entityType: 'hotel',
                });
                setViewedRoom(undefined);
              }}
              price={data.lowestPrice}
              isOpen={modalViewInfo.galleriesDetailsModalShown}
              view={modalViewInfo.view}
              room={viewedRoom}
            />
          ) : null}
        </>
      )}
    </Container>
  );
};

export default HotelDetail;
